import * as React from 'react';

import type { PurchaseLoadStatus } from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $completeInventoryScaleTicket,
  $completePriceScaleTicket,
  $revertScaleTicket,
  $scaleTicketDetail,
} from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';

export const STEP_ORDER: PurchaseLoadStatus[] = [
  'Open',
  'InventoryComplete',
  'PriceComplete',
  'Paid',
];

export function getForwardSteps(
  currentStatus: PurchaseLoadStatus,
  targetStatus: PurchaseLoadStatus,
): PurchaseLoadStatus[] {
  const currentIndex = STEP_ORDER.indexOf(currentStatus);
  const targetIndex = STEP_ORDER.indexOf(targetStatus);
  if (currentIndex === -1 || targetIndex === -1 || targetIndex <= currentIndex)
    return [];
  return STEP_ORDER.slice(currentIndex + 1, targetIndex + 1);
}

type UseStepperTransitionOptions = {
  ticketId: string | null | undefined;
  currentStatus: PurchaseLoadStatus;
};

export const useStepperTransition = ({
  ticketId,
  currentStatus,
}: UseStepperTransitionOptions) => {
  const t = useTranslations();

  const isTransitioningRef = React.useRef(false);

  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [isRevertDialogOpen, setIsRevertDialogOpen] = React.useState(false);
  const [pendingRevertTarget, setPendingRevertTarget] =
    React.useState<PurchaseLoadStatus | null>(null);

  const { refetch } = useAtomValue($scaleTicketDetail);
  const completeInventory = useSetAtom($completeInventoryScaleTicket);
  const completePrice = useSetAtom($completePriceScaleTicket);
  const revertStatus = useSetAtom($revertScaleTicket);

  const toast = useToast();

  const executeRevert = React.useCallback(
    async (targetStatus: PurchaseLoadStatus) => {
      if (!ticketId || isTransitioningRef.current) return;

      isTransitioningRef.current = true;
      setIsTransitioning(true);

      try {
        await revertStatus({ id: ticketId, targetStatus });
        toast.success(t.scaleTicketPro.detail.toast.revertStatusSuccess);
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err.message);
        }
        await refetch();
      } finally {
        isTransitioningRef.current = false;
        setIsTransitioning(false);
      }
    },
    [ticketId, revertStatus, refetch, toast, t],
  );

  const confirmRevert = React.useCallback(async () => {
    if (!pendingRevertTarget) return;
    setIsRevertDialogOpen(false);
    const target = pendingRevertTarget;
    setPendingRevertTarget(null);
    await executeRevert(target);
  }, [pendingRevertTarget, executeRevert]);

  const cancelRevert = React.useCallback(() => {
    setIsRevertDialogOpen(false);
    setPendingRevertTarget(null);
  }, []);

  const transitionTo = React.useCallback(
    async (targetStatus: PurchaseLoadStatus) => {
      if (!ticketId || isTransitioningRef.current) return;

      const currentIndex = STEP_ORDER.indexOf(currentStatus);
      const targetIndex = STEP_ORDER.indexOf(targetStatus);

      if (currentIndex === -1 || targetIndex === -1) return;
      if (currentIndex === targetIndex) return;

      if (targetIndex < currentIndex) {
        setPendingRevertTarget(targetStatus);
        setIsRevertDialogOpen(true);
        return;
      }

      isTransitioningRef.current = true;
      setIsTransitioning(true);

      try {
        const steps = getForwardSteps(currentStatus, targetStatus);

        for (const step of steps) {
          if (step === 'InventoryComplete') {
            await completeInventory({ id: ticketId });
          } else if (step === 'PriceComplete') {
            await completePrice({ id: ticketId });
          }
        }

        const lastStep = steps[steps.length - 1];
        toast.success(
          lastStep === 'InventoryComplete'
            ? t.scaleTicketPro.detail.toast.completeInventorySuccess
            : t.scaleTicketPro.detail.toast.completePricingSuccess,
        );
      } catch (err) {
        if (err instanceof Error) {
          await refetch();
          toast.error(
            t.scaleTicketPro.stepper.stepFailedAt(err.message, currentStatus),
          );
        }
      } finally {
        isTransitioningRef.current = false;
        setIsTransitioning(false);
      }
    },
    [
      ticketId,
      currentStatus,
      completeInventory,
      completePrice,
      refetch,
      toast,
      t,
    ],
  );

  return {
    transitionTo,
    isTransitioning,
    isRevertDialogOpen,
    pendingRevertTarget,
    confirmRevert,
    cancelRevert,
  };
};
